import { css } from '@emotion/react';
import { styled } from '@mui/material';
import { TableType } from '../model/rolesAndPerms.model';
import zeplinColor from '../theme/Color';

const DashboardTable: TableType = {
  id: 1213213231,
  columns: [
    {
      name: 'Dashboard',
      value: false,
      indeterminate: false,
    },
    {
      name: 'View',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Create',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Edit',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Delete',
      value: false,
      indeterminate: false,
    },
  ],
  rows: [
    { name: 'Activity', values: [false, false, false, false] },
    { name: 'Task', values: [false, false, false, false] },
    { name: 'Calendar', values: [false, false, false, false] },
  ],
};

const ClientsTable: TableType = {
  id: 1312321321321321,
  columns: [
    {
      name: 'Clients',
      value: false,
      indeterminate: false,
    },
    {
      name: 'View',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Create',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Edit',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Delete',
      value: false,
      indeterminate: false,
    },
  ],
  rows: [
    { name: 'Client Overview', values: [false, false, false, false] },
    { name: 'Activity', values: [false, false, false, false] },
    { name: 'Task', values: [false, false, false, false] },
    { name: 'Deals', values: [false, false, false, false] },
    { name: 'Social Engagement', values: [false, false, false, false] },
    { name: 'Contacts', values: [false, false, false, false] },
    { name: 'Box', values: [false, false, false, false] },
  ],
};

const TeamsTable: TableType = {
  id: 3213214142321,
  columns: [
    {
      name: 'Teams',
      value: false,
      indeterminate: false,
    },
    {
      name: 'View',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Create',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Edit',
      value: false,
      indeterminate: false,
    },
    {
      name: 'Delete',
      value: false,
      indeterminate: false,
    },
  ],
  rows: [
    { name: 'Members', values: [false, false, false, false] },
    { name: 'Profile', values: [false, false, false, false] },
    { name: 'Task', values: [false, false, false, false] },
  ],
};

export const tables: TableType[] = [DashboardTable, ClientsTable, TeamsTable];

export const Icon = styled('span')(
  css({
    borderRadius: 3,
    width: '24px',
    height: '24px',
    border: `1px solid ${zeplinColor.BasicBlack}`,
    '.Mui-focusVisible &': {
      outline: `2px auto ${zeplinColor.Brand1}`,
    },
    'input:hover ~ &': {
      backgroundColor: zeplinColor.Neutral4,
    },
  }),
);

export const CheckedIcon = styled(Icon)(
  css({
    backgroundColor: zeplinColor.BasicBlack,
    '&::before': {
      display: 'block',
      width: '100%',
      height: '100%',
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
        " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
        "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
      content: '""',
    },
    'input:hover ~ &': {
      backgroundColor: zeplinColor.BasicBlack,
    },
  }),
);

export const IndeterminateIcon = styled(Icon)(
  css({
    backgroundColor: zeplinColor.BasicBlack,

    '&::before': {
      display: 'block',
      width: '100%',
      height: '100%',
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Crect" +
        " x='3' y='7' width='10' height='2' rx='1' fill='%23fff'/%3E%3C/svg%3E\")",
      content: '""',
    },
    'input:hover ~ &': {
      backgroundColor: zeplinColor.BasicBlack,
    },
  }),
);

type ActionType = 'view' | 'create' | 'edit' | 'delete';

const ACTION_MAP: Record<ActionType, number> = {
  view: 0,
  create: 1,
  edit: 2,
  delete: 3,
};

export const transformPermissionsToTables = (
  allPermissions: any[],
  rolePermissions: any[] = [],
): TableType[] => {
  // Create a Set of IDs for fast lookup
  const rolePermIds = new Set(rolePermissions.map((p) => p.permissionId));

  // 1. Group all available permissions by Module and Name
  const grouped = allPermissions.reduce((acc: any, item: any) => {
    if (!acc[item.module]) acc[item.module] = {};
    if (!acc[item.module][item.name]) {
      acc[item.module][item.name] = [false, false, false, false];
    }

    const index = ACTION_MAP[item.action as ActionType];
    if (index !== undefined) {
      // Set true only if the role actually has this specific permission
      if (rolePermIds.has(item.permissionId)) {
        acc[item.module][item.name][index] = true;
      }
    }
    return acc;
  }, {});

  // 2. Map the grouped object into TableType structure
  return Object.keys(grouped).map((moduleName, idx) => {
    const rowData = Object.keys(grouped[moduleName]).map((rowName) => ({
      name: rowName,
      values: grouped[moduleName][rowName] as [boolean, boolean, boolean, boolean],
    }));

    // Generate columns and calculate initial header checkbox states (All/Indeterminate)
    const columns = [
      { name: moduleName, value: false, indeterminate: false }, // Module Header
      ...['View', 'Create', 'Edit', 'Delete'].map((colName, i) => {
        const allChecked = rowData.every((row) => row.values[i]);
        const someChecked = rowData.some((row) => row.values[i]);
        return {
          name: colName,
          value: allChecked,
          indeterminate: someChecked && !allChecked,
        };
      }),
    ];

    // Calculate the "Full Access" (index 0) header state
    const allRowsChecked = rowData.every((r) => r.values.every((v) => v));
    columns[0].value = allRowsChecked;

    return {
      id: idx + Date.now(),
      columns,
      rows: rowData,
    };
  });
};
